import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AppState,
  Image,
  Linking,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { doc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { auth, firestore } from '../firebase';
import { Colors, Radius, Shadow, Spacing, Typography } from '../theme';
import { getInstalledAppVersion } from '../utils/appVersion';
import {
  DEFAULT_APP_STORE_URL,
  DEFAULT_PLAY_STORE_URL,
  decideUpdatePrompt,
  toConfigString as toString,
  type UpdateLevel,
} from '../utils/appUpdateConfig';

export { decideUpdatePrompt };
import { useI18n } from '../i18n';
import { nudgeSession } from '../utils/nudgeSession';

// Remote source of truth: Firestore appConfig/update (public read, admin
// write — firestore.rules). Android fields predate this version and are also
// read by already-published Android builds, so keep their names/semantics:
//   active                               master switch ("updateEnabled")
//   latestAndroidVersionCode / Name      recommended target
//   minimumRequiredAndroidVersionCode    below this → required update
//   latestIosVersion                     recommended target (e.g. "1.0.3")
//   minimumSupportedIosVersion           below this → required update
//   updateTitle / updateMessage          recommended-update copy (optional)
//   requiredUpdateTitle / requiredUpdateMessage   required-update copy (optional)
//   playStoreUrl / appStoreUrl           store links (defaults below)
// `forceUpdate` is deliberately ignored here: published Android builds treat
// it as "force everyone below latest", which Tonyte must never do — keep it
// false and use the minimum-supported fields for genuinely unsupported builds.
const CONFIG_CACHE_KEY = '@tonyte/app-update-config';
const SNOOZE_PREFIX = '@tonyte/app-update-snooze/';
const SNOOZE_MS = 3 * 24 * 60 * 60 * 1000;
const RECHECK_MIN_INTERVAL_MS = 30 * 60 * 1000;
const UPDATE_CHECK_DELAY_MS = 5000;
const DEBUG_UPDATE_CHECK = false;

type PromptState = {
  level: UpdateLevel;
  target: string; // version the user is asked to install, for snooze/analytics
  targetLabel: string;
  title: string;
  message: string;
  storeUrl: string;
};

type RawConfig = Record<string, unknown>;

// Minimal, non-invasive prompt analytics: last event per kind on the user's
// own doc (allowed by firestore.rules' own-profile update rule).
function trackUpdatePrompt(event: 'shown' | 'update_clicked' | 'dismissed', prompt: PromptState) {
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  updateDoc(doc(firestore, 'users', uid), {
    appUpdatePrompt: {
      event: `update_prompt_${event}`,
      level: prompt.level,
      target: prompt.target,
      platform: Platform.OS,
      at: serverTimestamp(),
    },
  }).catch(() => undefined);
}

async function loadConfig(): Promise<RawConfig | null> {
  try {
    const snap = await getDoc(doc(firestore, 'appConfig', 'update'));
    const data = snap.exists() ? (snap.data() as RawConfig) : null;
    await AsyncStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify(data)).catch(() => undefined);
    return data;
  } catch (error) {
    // Offline / Firestore unavailable: fall back to the last config this
    // device saw (so a required update stays required offline), else nothing.
    if (__DEV__) console.warn('[Tonyte][UpdateCheck] config fetch failed, using cache', error);
    try {
      const cached = await AsyncStorage.getItem(CONFIG_CACHE_KEY);
      return cached ? (JSON.parse(cached) as RawConfig | null) : null;
    } catch {
      return null;
    }
  }
}

const AppUpdatePrompt: React.FC<{ enabled: boolean }> = ({ enabled }) => {
  const { t } = useI18n();
  const [prompt, setPrompt] = useState<PromptState | null>(null);
  const [openingStore, setOpeningStore] = useState(false);
  const lastCheckRef = useRef(0);
  const checkingRef = useRef(false);

  const checkForUpdate = useCallback(async () => {
    if (checkingRef.current) return;
    checkingRef.current = true;
    lastCheckRef.current = Date.now();
    try {
      const installed = await getInstalledAppVersion();
      if (!installed) return;
      const config = await loadConfig();
      if (!config) return;
      const decision = decideUpdatePrompt(config, installed);
      if (DEBUG_UPDATE_CHECK) console.log('[Tonyte][UpdateCheck]', { installed, decision });
      if (!decision) {
        setPrompt(null);
        return;
      }
      if (decision.level === 'recommended') {
        const snoozedAt = Number(await AsyncStorage.getItem(`${SNOOZE_PREFIX}${installed.platform}:${decision.target}`));
        if (Number.isFinite(snoozedAt) && Date.now() - snoozedAt < SNOOZE_MS) return;
      }
      const required = decision.level === 'required';
      const next: PromptState = {
        ...decision,
        title: required
          ? toString(config.requiredUpdateTitle, t('appUpdate.requiredTitle'))
          : toString(config.updateTitle, t('appUpdate.optionalTitle')),
        message: required
          ? toString(config.requiredUpdateMessage, t('appUpdate.requiredMessage'))
          : toString(config.updateMessage, t('appUpdate.optionalMessage')),
        storeUrl: installed.platform === 'ios'
          ? toString(config.appStoreUrl, DEFAULT_APP_STORE_URL)
          : toString(config.playStoreUrl, DEFAULT_PLAY_STORE_URL),
      };
      setPrompt(current => {
        if (!current || current.target !== next.target || current.level !== next.level) trackUpdatePrompt('shown', next);
        return next;
      });
    } catch (error) {
      if (__DEV__) console.warn('[Tonyte][UpdateCheck] skipped', error);
    } finally {
      checkingRef.current = false;
    }
  }, [t]);

  // On startup (after a short delay, off the launch path) and whenever the
  // app returns to the foreground, at most every RECHECK_MIN_INTERVAL_MS.
  useEffect(() => {
    if (!enabled) {
      setPrompt(null);
      return undefined;
    }
    const timer = setTimeout(() => { void checkForUpdate(); }, UPDATE_CHECK_DELAY_MS);
    const subscription = AppState.addEventListener('change', state => {
      if (state === 'active' && Date.now() - lastCheckRef.current >= RECHECK_MIN_INTERVAL_MS) void checkForUpdate();
    });
    return () => {
      clearTimeout(timer);
      subscription.remove();
    };
  }, [checkForUpdate, enabled]);

  // Critical notice: optional nudges never appear while this is on screen.
  useEffect(() => {
    nudgeSession.setOverlay('appUpdate', Boolean(prompt));
    return () => nudgeSession.setOverlay('appUpdate', false);
  }, [prompt]);

  const required = prompt?.level === 'required';

  const versionLabel = useMemo(() => {
    if (!prompt?.targetLabel) return t('appUpdate.newVersion');
    return t('appUpdate.newVersionNamed').replace('{version}', prompt.targetLabel);
  }, [prompt?.targetLabel, t]);

  const dismiss = useCallback(async () => {
    if (!prompt || prompt.level === 'required') return;
    trackUpdatePrompt('dismissed', prompt);
    await AsyncStorage.setItem(`${SNOOZE_PREFIX}${Platform.OS}:${prompt.target}`, String(Date.now())).catch(() => undefined);
    setPrompt(null);
  }, [prompt]);

  const updateNow = useCallback(async () => {
    if (!prompt || openingStore) return;
    trackUpdatePrompt('update_clicked', prompt);
    setOpeningStore(true);
    try {
      await Linking.openURL(prompt.storeUrl);
    } catch (error) {
      if (__DEV__) console.warn('[Tonyte][UpdateCheck] store link failed', { url: prompt.storeUrl, error });
    } finally {
      setOpeningStore(false);
    }
  }, [openingStore, prompt]);

  if (!prompt) return null;

  return (
    <Modal
      transparent
      statusBarTranslucent
      animationType="fade"
      visible
      onRequestClose={required ? () => undefined : dismiss}
    >
      <View style={styles.overlay}>
        <Pressable style={StyleSheet.absoluteFill} onPress={required ? undefined : dismiss} />
        <View style={styles.card}>
          <LinearGradient
            colors={['rgba(255,255,255,0.12)', 'rgba(255,255,255,0.045)', 'rgba(5,8,16,0.96)']}
            locations={[0, 0.42, 1]}
            style={StyleSheet.absoluteFill}
          />
          <LinearGradient
            colors={['rgba(242,125,38,0.28)', 'rgba(242,125,38,0.08)', 'rgba(242,125,38,0)']}
            locations={[0, 0.42, 1]}
            start={{ x: 0.5, y: 0 }}
            end={{ x: 0.5, y: 1 }}
            style={styles.orangeGlow}
          />
          <View style={styles.inner}>
            <View style={styles.logoHalo}>
              <Image source={require('../assets/login_logo.png')} style={styles.logo} resizeMode="contain" />
            </View>
            <Text style={styles.title}>{prompt.title}</Text>
            <Text style={styles.message}>{prompt.message}</Text>
            <View style={styles.versionPill}>
              <Icon name="arrow-up-circle" size={14} color={Colors.sunset} />
              <Text style={styles.versionText}>{versionLabel}</Text>
            </View>

            <TouchableOpacity
              activeOpacity={0.86}
              style={[styles.primaryButton, openingStore && styles.buttonDisabled]}
              onPress={updateNow}
              disabled={openingStore}
            >
              <Text style={styles.primaryButtonText}>
                {openingStore ? t('appUpdate.opening') : required ? t('appUpdate.updateTonyte') : t('appUpdate.updateNow')}
              </Text>
              <Icon name="external-link" size={16} color="#fff" />
            </TouchableOpacity>

            {required ? null : (
              <TouchableOpacity activeOpacity={0.8} style={styles.secondaryButton} onPress={dismiss}>
                <Text style={styles.secondaryButtonText}>{t('appUpdate.notNow')}</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: Spacing.lg,
    backgroundColor: 'rgba(2,5,12,0.78)',
  },
  card: {
    width: '100%',
    maxWidth: 390,
    borderRadius: Radius.xxl,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
    backgroundColor: 'rgba(7,11,20,0.96)',
    ...Shadow.strong,
  },
  orangeGlow: {
    position: 'absolute',
    left: -60,
    right: -60,
    top: -78,
    height: 210,
  },
  inner: {
    padding: Spacing.xl,
    alignItems: 'center',
    gap: Spacing.md,
  },
  logoHalo: {
    width: 78,
    height: 78,
    borderRadius: 39,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.16)',
    shadowColor: Colors.sunset,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: Platform.OS === 'ios' ? 0.42 : 0.58,
    shadowRadius: 22,
    elevation: 12,
  },
  logo: {
    width: 52,
    height: 52,
  },
  title: {
    color: Colors.textPrimary,
    fontSize: 22,
    lineHeight: 27,
    fontWeight: Typography.extrabold,
    textAlign: 'center',
  },
  message: {
    color: 'rgba(255,255,255,0.68)',
    fontSize: Typography.base,
    lineHeight: 22,
    textAlign: 'center',
  },
  versionPill: {
    minHeight: 34,
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
    paddingHorizontal: Spacing.md,
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(242,125,38,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(242,125,38,0.22)',
  },
  versionText: {
    color: '#ffd6b8',
    fontSize: Typography.sm,
    fontWeight: Typography.semibold,
  },
  primaryButton: {
    width: '100%',
    minHeight: 52,
    borderRadius: Radius.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: Spacing.sm,
    marginTop: Spacing.sm,
    backgroundColor: Colors.sunset,
    shadowColor: Colors.sunset,
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: Platform.OS === 'ios' ? 0.28 : 0.44,
    shadowRadius: 18,
    elevation: 10,
  },
  buttonDisabled: {
    opacity: 0.72,
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: Typography.base,
    fontWeight: Typography.bold,
  },
  secondaryButton: {
    minHeight: 44,
    paddingHorizontal: Spacing.xl,
    alignItems: 'center',
    justifyContent: 'center',
  },
  secondaryButtonText: {
    color: 'rgba(255,255,255,0.58)',
    fontSize: Typography.sm,
    fontWeight: Typography.semibold,
  },
});

export default AppUpdatePrompt;
